import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldAlert, RefreshCw, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import {
  listModeration,
  triggerModerationScan,
  setBlocked,
} from "@/lib/moderation.functions";

type Filter = "flagged" | "pending" | "ok" | "error" | "all";

export const Route = createFileRoute("/_authenticated/admin_/moderation")({
  head: () => ({
    meta: [
      { title: "モデレーション監視 | 管理" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ModerationPage,
});

function ModerationPage() {
  const isAdminFn = useServerFn(getIsAdmin);
  const listFn = useServerFn(listModeration);
  const triggerFn = useServerFn(triggerModerationScan);
  const blockFn = useServerFn(setBlocked);

  const [checked, setChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState<Filter>("flagged");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const auth = async () => {
    const { data } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${data.session?.access_token ?? ""}` };
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = await auth();
        const r = await isAdminFn({ headers });
        setIsAdmin(r.isAdmin);
      } finally {
        setChecked(true);
      }
    })();
  }, [isAdminFn]);

  const load = async (f: Filter) => {
    setLoading(true);
    try {
      const headers = await auth();
      const r = await listFn({ data: { filter: f, limit: 50 }, headers });
      setRows(r.rows);
    } catch (e: any) {
      toast.error(e?.message ?? "読み込み失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, filter]);

  const onScan = async () => {
    setScanning(true);
    try {
      const headers = await auth();
      const r: any = await triggerFn({ headers });
      toast.success(`完了: ${r.processed}件処理 / ${r.flagged}件フラグ`);
      load(filter);
    } catch (e: any) {
      toast.error(e?.message ?? "スキャン失敗");
    } finally {
      setScanning(false);
    }
  };

  const onBlock = async (id: string, is_blocked: boolean) => {
    try {
      const headers = await auth();
      await blockFn({ data: { id, is_blocked }, headers });
      toast.success(is_blocked ? "ブロックしました" : "ブロック解除");
      load(filter);
    } catch (e: any) {
      toast.error(e?.message ?? "更新失敗");
    }
  };

  if (!checked) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }
  if (!isAdmin) {
    return (
      <main className="min-h-screen px-4 py-16">
        <Card className="mx-auto max-w-md p-8 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-4 text-2xl font-bold">アクセス権がありません</h1>
          <Button asChild className="mt-6">
            <Link to="/dashboard">ダッシュボードへ</Link>
          </Button>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">モデレーション監視</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              生成された動画/画像を AI で自動審査。1時間ごとに自動スキャン。
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/admin">← 管理画面</Link>
            </Button>
            <Button onClick={onScan} disabled={scanning}>
              {scanning ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              今すぐスキャン
            </Button>
          </div>
        </div>

        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)} className="mt-6">
          <TabsList>
            <TabsTrigger value="flagged">⚠️ フラグ</TabsTrigger>
            <TabsTrigger value="pending">未審査</TabsTrigger>
            <TabsTrigger value="ok">OK</TabsTrigger>
            <TabsTrigger value="error">エラー</TabsTrigger>
            <TabsTrigger value="all">全て</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="mt-4 p-2">
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground">該当なし</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>プレビュー</TableHead>
                  <TableHead>状態</TableHead>
                  <TableHead>スコア</TableHead>
                  <TableHead>理由</TableHead>
                  <TableHead>ユーザー</TableHead>
                  <TableHead>審査日時</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const url = r.kind === "image" ? r.output_image_url : r.output_video_url;
                  return (
                    <TableRow key={r.id}>
                      <TableCell>
                        {url ? (
                          r.kind === "image" ? (
                            <img src={url} alt="" className="h-16 w-16 rounded object-cover" />
                          ) : (
                            <video src={url} className="h-16 w-16 rounded object-cover" muted />
                          )
                        ) : (
                          <span className="text-xs text-muted-foreground">なし</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            r.moderation_status === "flagged"
                              ? "destructive"
                              : r.moderation_status === "ok"
                                ? "default"
                                : "secondary"
                          }
                        >
                          {r.moderation_status}
                        </Badge>
                        {r.is_blocked && (
                          <Badge variant="destructive" className="ml-1">
                            非公開
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">
                        {r.moderation_score != null ? Number(r.moderation_score).toFixed(2) : "-"}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="truncate text-xs">{r.moderation_reason ?? "-"}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {r.prompt_original ?? ""}
                        </p>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.user_id.slice(0, 8)}</TableCell>
                      <TableCell className="text-xs">
                        {r.moderation_checked_at
                          ? new Date(r.moderation_checked_at).toLocaleString("ja-JP")
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.is_blocked ? (
                          <Button size="sm" variant="outline" onClick={() => onBlock(r.id, false)}>
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            解除
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => onBlock(r.id, true)}
                          >
                            <Ban className="mr-1 h-3 w-3" />
                            ブロック
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </main>
  );
}
